import React, { useCallback, useEffect, useMemo, useState, ReactNode } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import {
  DraftManual,
  ManualPermissions,
  TransformedPendingUpdates,
} from 'views/App/Portal/Policy/_types/manuals/draft';
import useStickyState from 'hooks/useStickyState';
import useDraftManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftManual';
import useManageManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManageManual';
import usePendingUpdates from 'views/App/Portal/Policy/DraftManuals/_data_sources/usePendingUpdates';

type DraftManualDetailProviderProps = {
  children: React.ReactNode;
};

export type DraftManualDetailProviderResponse = {
  manualPermissions: ManualPermissions | null;
  setManualPermissions: (manualPermissions: ManualPermissions | null) => void;
  manualHasPermissions: (manualId: string, keys: string[]) => boolean;
  manualId: string;
  manual: DraftManual;
  manualQuery: any;
  node: string | undefined;
  setNode: (nodeValue: string) => void;
  tocExpandedItems: string[];
  setTocExpandedItems: React.Dispatch<React.SetStateAction<string[]>>;
  editorType: string;
  pendingUpdates: TransformedPendingUpdates | null | undefined;
  hasPolicyPendingUpdates: (policyId: string) => boolean;
  isAttachmentLibraryOpen: boolean;
  setIsAttachmentLibraryOpen: (isAttachmentLibraryOpen: boolean) => void;
  isComparePolicyOpen: boolean;
  setIsComparePolicyOpen: (isComparePolicyOpen: boolean) => void;
  showAccreditationTags: boolean;
  setShowAccreditationTags: (show: boolean) => void;
  enabledFilter: boolean;
  setEnabledFilter: (enabled: boolean) => void;
  disabledFilter: boolean;
  setDisabledFilter: (disabled: boolean) => void;
};

export const DraftManualDetailContext = React.createContext<DraftManualDetailProviderResponse>(
  {} as DraftManualDetailProviderResponse
);

const TOC_EXPANDED_STORAGE_PREFIX = 'phx-draft-manuals-toc-expanded';

const DraftManualDetailProvider: ({ children }: DraftManualDetailProviderProps) => any = ({ children }) => {
  const { id, node: nodeParam } = useParams();
  const location = useLocation();
  const [nodeOverride, setNodeOverride] = useState<string | null>(null);
  const node = (nodeOverride ?? location.hash?.slice(1) ?? nodeParam) as string | undefined;

  const tocStorageKey = `${TOC_EXPANDED_STORAGE_PREFIX}-${id ?? 'no-manual'}`;
  const [tocExpandedItems, setTocExpandedItems] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(tocStorageKey);
      const parsed = raw ? (JSON.parse(raw) as string[]) : null;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(tocStorageKey);
      const parsed = raw ? (JSON.parse(raw) as string[]) : null;
      if (Array.isArray(parsed) && (parsed.length > 0 || !tocStorageKey.endsWith('no-manual'))) {
        setTocExpandedItems(parsed);
      }
    } catch {
      /* ignore */
    }
  }, [tocStorageKey]);

  useEffect(() => {
    try {
      if (tocExpandedItems.length > 0) {
        localStorage.setItem(tocStorageKey, JSON.stringify(tocExpandedItems));
      }
    } catch {
      /* ignore */
    }
  }, [tocStorageKey, tocExpandedItems]);

  useEffect(() => {
    setNodeOverride(null);
  }, [location.pathname, id]);

  const setNode = useCallback(
    (nodeValue: string) => {
      setNodeOverride(nodeValue);
      const url = `${location.pathname}${location.search ?? ''}#${nodeValue}`;
      window.history.replaceState(null, '', url);
    },
    [location.pathname, location.search]
  );

  const [editorType, setEditorType] = React.useState<'preface' | 'policy'>('policy');
  const [pendingUpdates, setPendingUpdates] = useState<TransformedPendingUpdates | null>();
  const [isAttachmentLibraryOpen, setIsAttachmentLibraryOpen] = useState(false);
  const [isComparePolicyOpen, setIsComparePolicyOpen] = useState(false);
  const [showAccreditationTags, setShowAccreditationTags] = useStickyState('phx-showAccreditationTags', true);
  const [enabledFilter, setEnabledFilter] = useStickyState<boolean>('phx-policy-editor-enabled-filter', true);
  const [disabledFilter, setDisabledFilter] = useStickyState<boolean>('phx-policy-editor-disabled-filter', false);

  React.useEffect(() => {
    if (node) {
      const [type] = node.split(':');
      if (type === 'preface' || type === 'policy') {
        setEditorType(type);
      }
    }
  }, [node]);

  const manualQuery = useDraftManual(id, {
    onSuccess: (data: DraftManual) => {
      if (data) {
        setManualPermissions((prev) => {
          prev = prev ?? {};
          prev[data.id] = (data.authorizations ?? []).map((x) => x.action).sort();
          return prev;
        });
      }
    },
  });
  const [manualPermissions, setManualPermissions] = useStickyState<ManualPermissions | null>(
    'phx-manualPermissions',
    null
  );

  const manualHasPermissions = useCallback(
    (manualId: string, keys: string[]) => {
      let permissionKeys: string[] = [];
      if (manualPermissions && manualPermissions[manualId]) {
        permissionKeys = manualPermissions[manualId];
      }
      return keys.every((x) => permissionKeys.map((x) => x.toLowerCase()).includes(x.toLowerCase()));
    },
    [manualPermissions]
  );

  const manual = useMemo(() => {
    return manualQuery.data ?? ({} as DraftManual);
  }, [manualQuery.data]);

  const pendingUpdatesQuery = usePendingUpdates(manual);
  useEffect(() => {
    setPendingUpdates(pendingUpdatesQuery.data);
  }, [pendingUpdatesQuery.data, manualQuery.data]);

  const hasPolicyPendingUpdates = useCallback(
    (policyId: string): boolean => {
      if (!pendingUpdates) return false;
      const policy = pendingUpdates.policies[policyId];
      return !!(policy && policy.count > 0);
    },
    [pendingUpdates]
  );

  const showAttachmentLibrary = useCallback(() => setIsAttachmentLibraryOpen(true), []);
  const hideAttachmentLibrary = useCallback(() => setIsAttachmentLibraryOpen(false), []);

  const contextValue: DraftManualDetailProviderResponse = useMemo(
    () => ({
      manualPermissions,
      manualHasPermissions,
      setManualPermissions,
      manualId: id ?? '',
      manual,
      manualQuery,
      node,
      setNode,
      tocExpandedItems,
      setTocExpandedItems,
      editorType,
      pendingUpdates,
      hasPolicyPendingUpdates,
      isAttachmentLibraryOpen,
      setIsAttachmentLibraryOpen,
      isComparePolicyOpen,
      setIsComparePolicyOpen,
      showAccreditationTags,
      setShowAccreditationTags,
      enabledFilter,
      setEnabledFilter,
      disabledFilter,
      setDisabledFilter,
    }),
    [
      manualPermissions,
      manualHasPermissions,
      setManualPermissions,
      id,
      manual,
      manualQuery,
      node,
      setNode,
      tocExpandedItems,
      setTocExpandedItems,
      pendingUpdates,
      isAttachmentLibraryOpen,
      setIsAttachmentLibraryOpen,
      isComparePolicyOpen,
      setIsComparePolicyOpen,
      showAccreditationTags,
      setShowAccreditationTags,
      enabledFilter,
      setEnabledFilter,
      disabledFilter,
      setDisabledFilter,
    ]
  );

  return <DraftManualDetailContext.Provider value={contextValue}>{children}</DraftManualDetailContext.Provider>;
};

export default DraftManualDetailProvider;

interface DraftManualsDetailContextType {
  isAttachmentLibraryOpen: boolean;
  showAttachmentLibrary: () => void;
  hideAttachmentLibrary: () => void;
}

const DraftManualsDetailContext = React.createContext<DraftManualsDetailContextType | undefined>(undefined);

export const useDraftManualsDetail = () => {
  const context = React.useContext(DraftManualsDetailContext);
  if (!context) {
    throw new Error('useDraftManualsDetail must be used within a DraftManualsDetailProvider');
  }
  return context;
};

interface DraftManualsDetailProviderProps {
  children: ReactNode;
}

export const DraftManualsDetailProvider: React.FC<DraftManualsDetailProviderProps> = ({ children }) => {
  const [isAttachmentLibraryOpen, setIsAttachmentLibraryOpen] = useState(false);

  const showAttachmentLibrary = () => setIsAttachmentLibraryOpen(true);
  const hideAttachmentLibrary = () => setIsAttachmentLibraryOpen(false);

  return (
    <DraftManualsDetailContext.Provider
      value={{
        isAttachmentLibraryOpen,
        showAttachmentLibrary,
        hideAttachmentLibrary,
      }}
    >
      {children}
    </DraftManualsDetailContext.Provider>
  );
};
